import { SyntheticEvent, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";

import { formatDate } from "@/global/utils/utils";
import {
  paymentPlansState,
  selectPaymentPlans,
  selectPaymentPlanSelected,
} from "@/global/store/data/purchaseSeats/paymentPlans";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { useAppDispatch, useAppStore } from "@/global/store";
import { getPaymentPlansApi } from "@/global/api/paymentPlans";
import { selectCredit } from "@/global/store/data/purchaseSeats/credit";
import { selectServiceFeeTotal } from "@/global/store/data/purchaseSeats/serviceFee";

import type { PaymentPlansType } from "@/types";

export function usePaymentPlans() {
  const dispatch = useAppDispatch();
  const { creditToUse } = useAppStore(selectCredit);
  const paymentPlans = useAppStore(selectPaymentPlans);
  const getPaymentPlans = useMutation(getPaymentPlansApi);
  const { teamSchema, availableCreditEnabled } = useTeamConfig();
  const serviceFeeTotalAmount = useAppStore(selectServiceFeeTotal);
  const selectedPaymentPlan = useAppStore(selectPaymentPlanSelected);

  // prettier-ignore
  async function handleGetPaymentPlans(totalAmount: number) {
    try {
      // if creditToUse is greater than or equal to totalAmount, then don't call get payment plans
      if (availableCreditEnabled && creditToUse > 0 && creditToUse >= totalAmount) {
        return dispatch(paymentPlansState.actions.set([]));
      }

      const payload = { teamSchema, totalAmount, creditToUse };
      const { data } = await getPaymentPlans.mutateAsync(payload);
      // need to add pay in full option to this data array
      if (data.length > 0) {
        let payInFull: PaymentPlansType = {
          id: 0,
          title: "Pay in Full",
          isDynamic: true,
          totalInstallments: 1,
          installments: [
            {
              amount: totalAmount,
              dueDate: "today",
            },
          ],
          selected: true,
          totalAmount: serviceFeeTotalAmount,
        };

        let paymentPlans: PaymentPlansType[] = (data as PaymentPlansType[]).map(
          (paymentPlan) => ({
            ...paymentPlan,
            totalAmount: serviceFeeTotalAmount,
            installments: paymentPlan.installments.map((installment) => ({
              ...installment,
              dueDate: formatDate(installment.dueDate, true) as string,
            })),
          })
        );

        if (availableCreditEnabled && creditToUse > 0 && creditToUse < totalAmount) {
          payInFull.totalAmount = serviceFeeTotalAmount - creditToUse;
          payInFull.installments[0].amount = payInFull.totalAmount;
          paymentPlans = paymentPlans.map((paymentPlan) => ({
            ...paymentPlan,
            totalAmount: (paymentPlan?.totalAmount || 0) - creditToUse,
          }));
        }

        dispatch(paymentPlansState.actions.set([payInFull, ...paymentPlans]));
      }
    } catch (error: any) {
      console.log(error);
      dispatch(paymentPlansState.actions.set([]));
    }
  }

  function handleChangPaymentPlan(event: SyntheticEvent<Element, Event>) {
    const target = event.target as HTMLInputElement;
    const selected = Number(target.defaultValue);
    dispatch(
      paymentPlansState.actions.set(
        paymentPlans.map((itm) => ({
          ...itm,
          selected: itm.id === selected,
        }))
      )
    );
  }

  return {
    paymentPlans,
    getPaymentPlans,
    selectedPaymentPlan,
    handleGetPaymentPlans,
    handleChangPaymentPlan,
  };
}
